import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useCompany } from "./CompanyContext";
import { useAccounting } from "./AccountingContext";

// ========================
// TYPES
// ========================

export type NotificationType = "gst_deadline" | "low_inventory" | "unpaid_invoice" | "subscription_renewal" | "eway_expiry" | "system" | "approval";
export type NotificationPriority = "critical" | "high" | "medium" | "low";
export type NotificationChannel = "dashboard" | "email" | "mobile";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  channels: NotificationChannel[];
  isRead: boolean;
  isDismissed: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, string>;
  createdAt: string;
  readAt?: string;
}

export interface NotificationPreference {
  type: NotificationType;
  label: string;
  dashboard: boolean;
  email: boolean;
  mobile: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreference[] = [
  { type: "gst_deadline", label: "GST Filing Deadlines", dashboard: true, email: true, mobile: true },
  { type: "low_inventory", label: "Low Inventory Alerts", dashboard: true, email: true, mobile: false },
  { type: "unpaid_invoice", label: "Unpaid Invoices", dashboard: true, email: true, mobile: false },
  { type: "subscription_renewal", label: "Subscription Renewal", dashboard: true, email: true, mobile: false },
  { type: "eway_expiry", label: "E-Way Bill Expiry", dashboard: true, email: true, mobile: true },
  { type: "system", label: "System Notifications", dashboard: true, email: false, mobile: false },
  { type: "approval", label: "Approval Requests", dashboard: true, email: false, mobile: false },
];

interface NotificationContextType {
  notifications: Notification[];
  preferences: NotificationPreference[];
  unreadCount: number;
  criticalCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  addNotification: (data: Omit<Notification, "id" | "createdAt" | "isRead" | "isDismissed">) => void;
  updatePreference: (type: NotificationType, channel: NotificationChannel, enabled: boolean) => void;
  getByType: (type: NotificationType) => Notification[];
  getUnread: () => Notification[];
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { activeCompany } = useCompany();
  const { vouchers, stockItems } = useAccounting();

  const [preferences, setPreferences] = useState<NotificationPreference[]>(DEFAULT_PREFERENCES);
  const [customNotifications, setCustomNotifications] = useState<Notification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const companyId = activeCompany?.id || "default";

  // Load persisted read/dismiss state for active company
  useEffect(() => {
    try {
      const savedRead = localStorage.getItem(`accoupro_notifs_read_${companyId}`);
      if (savedRead) setReadIds(new Set(JSON.parse(savedRead)));
      else setReadIds(new Set());

      const savedDismissed = localStorage.getItem(`accoupro_notifs_dismissed_${companyId}`);
      if (savedDismissed) setDismissedIds(new Set(JSON.parse(savedDismissed)));
      else setDismissedIds(new Set());
    } catch {
      setReadIds(new Set());
      setDismissedIds(new Set());
    }
  }, [companyId]);

  // Derive genuine dynamic notifications strictly from real company records
  const dynamicNotifications = useMemo<Notification[]>(() => {
    if (!activeCompany) return [];
    const notifs: Notification[] = [];
    const now = new Date();

    // 1. Genuine GST Deadlines — ONLY if GST registered with a valid GSTIN
    const hasValidGst = Boolean(
      activeCompany.gstRegistered &&
      activeCompany.gstin &&
      activeCompany.gstin.trim().length === 15 &&
      !activeCompany.gstin.startsWith("00") &&
      !activeCompany.gstin.startsWith("22AAAAA")
    );

    if (hasValidGst) {
      const currentDay = now.getDate();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      // GSTR-1: Due 11th of month
      if (currentDay >= 6 && currentDay <= 11) {
        const daysLeft = 11 - currentDay;
        const dueDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-11`;
        notifs.push({
          id: `gst-gstr1-${dueDateStr}`,
          type: "gst_deadline",
          title: daysLeft === 0 ? "GSTR-1 Due Today" : `GSTR-1 Filing Due in ${daysLeft} Day${daysLeft > 1 ? "s" : ""}`,
          message: `Monthly GSTR-1 return for outward supplies is due on the 11th. Ensure all sales vouchers are verified.`,
          priority: daysLeft <= 2 ? "critical" : "high",
          channels: ["dashboard", "email"],
          isRead: false,
          isDismissed: false,
          actionUrl: "/dashboard/gst",
          actionLabel: "View GST Returns",
          metadata: { returnType: "GSTR-1", dueDate: dueDateStr },
          createdAt: new Date(currentYear, currentMonth, 6, 9, 0, 0).toISOString(),
        });
      }

      // GSTR-3B: Due 20th of month
      if (currentDay >= 15 && currentDay <= 20) {
        const daysLeft = 20 - currentDay;
        const dueDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-20`;
        notifs.push({
          id: `gst-gstr3b-${dueDateStr}`,
          type: "gst_deadline",
          title: daysLeft === 0 ? "GSTR-3B Due Today" : `GSTR-3B Filing Due in ${daysLeft} Day${daysLeft > 1 ? "s" : ""}`,
          message: `Monthly summary GSTR-3B return is due on the 20th. Reconcile output tax and eligible ITC before filing.`,
          priority: daysLeft <= 2 ? "critical" : "high",
          channels: ["dashboard", "email"],
          isRead: false,
          isDismissed: false,
          actionUrl: "/dashboard/gst",
          actionLabel: "File GSTR-3B",
          metadata: { returnType: "GSTR-3B", dueDate: dueDateStr },
          createdAt: new Date(currentYear, currentMonth, 15, 9, 0, 0).toISOString(),
        });
      }
    }

    // 2. Genuine Low Stock Alerts — ONLY if items actually exist and are below reorder level
    if (stockItems && stockItems.length > 0) {
      const lowStock = stockItems.filter(
        (i) => i.reorderLevel && Number(i.reorderLevel) > 0 && Number(i.currentQty) <= Number(i.reorderLevel)
      );

      for (const item of lowStock.slice(0, 5)) {
        notifs.push({
          id: `stock-low-${item.id}`,
          type: "low_inventory",
          title: `Low Stock: ${item.name}`,
          message: `${item.name} has ${item.currentQty} ${item.unit || "units"} remaining (reorder threshold: ${item.reorderLevel}).`,
          priority: Number(item.currentQty) === 0 ? "critical" : "high",
          channels: ["dashboard"],
          isRead: false,
          isDismissed: false,
          actionUrl: "/dashboard/inventory",
          actionLabel: "Reorder",
          metadata: { itemName: item.name, currentQty: String(item.currentQty), reorderLevel: String(item.reorderLevel) },
          createdAt: item.updatedAt || new Date().toISOString(),
        });
      }
    }

    // 3. Genuine Pending Vouchers for Review
    if (vouchers && vouchers.length > 0) {
      const pending = vouchers.filter((v) => v.status === "Pending");
      if (pending.length > 0) {
        notifs.push({
          id: `vouchers-pending-${pending.length}`,
          type: "approval",
          title: `${pending.length} Voucher${pending.length > 1 ? "s" : ""} Awaiting Review`,
          message: `There are ${pending.length} unapproved accounting vouchers waiting for company admin or CA approval.`,
          priority: "medium",
          channels: ["dashboard"],
          isRead: false,
          isDismissed: false,
          actionUrl: "/dashboard/journals",
          actionLabel: "Review Entries",
          metadata: { count: String(pending.length) },
          createdAt: pending[0].createdAt || new Date().toISOString(),
        });
      }
    }

    return notifs;
  }, [activeCompany, stockItems, vouchers]);

  // Combine dynamic + custom notifications with persisted read & dismiss status
  const notifications = useMemo<Notification[]>(() => {
    const combined = [...customNotifications, ...dynamicNotifications];
    return combined.map((n) => ({
      ...n,
      isRead: n.isRead || readIds.has(n.id),
      isDismissed: n.isDismissed || dismissedIds.has(n.id),
    }));
  }, [customNotifications, dynamicNotifications, readIds, dismissedIds]);

  const unreadCount = notifications.filter((n) => !n.isRead && !n.isDismissed).length;
  const criticalCount = notifications.filter((n) => !n.isRead && !n.isDismissed && n.priority === "critical").length;

  const markAsRead = useCallback((id: string) => {
    setReadIds((prev) => {
      const updated = new Set(prev).add(id);
      try {
        localStorage.setItem(`accoupro_notifs_read_${companyId}`, JSON.stringify(Array.from(updated)));
      } catch {}
      return updated;
    });
  }, [companyId]);

  const markAllAsRead = useCallback(() => {
    const allIds = notifications.filter((n) => !n.isDismissed).map((n) => n.id);
    setReadIds((prev) => {
      const updated = new Set([...prev, ...allIds]);
      try {
        localStorage.setItem(`accoupro_notifs_read_${companyId}`, JSON.stringify(Array.from(updated)));
      } catch {}
      return updated;
    });
  }, [notifications, companyId]);

  const dismiss = useCallback((id: string) => {
    setDismissedIds((prev) => {
      const updated = new Set(prev).add(id);
      try {
        localStorage.setItem(`accoupro_notifs_dismissed_${companyId}`, JSON.stringify(Array.from(updated)));
      } catch {}
      return updated;
    });
  }, [companyId]);

  const dismissAll = useCallback(() => {
    const allIds = notifications.map((n) => n.id);
    setDismissedIds((prev) => {
      const updated = new Set([...prev, ...allIds]);
      try {
        localStorage.setItem(`accoupro_notifs_dismissed_${companyId}`, JSON.stringify(Array.from(updated)));
      } catch {}
      return updated;
    });
  }, [notifications, companyId]);

  const addNotification = useCallback((data: Omit<Notification, "id" | "createdAt" | "isRead" | "isDismissed">) => {
    const newNotif: Notification = {
      ...data,
      id: `notif-${crypto.randomUUID().slice(0, 8)}`,
      isRead: false,
      isDismissed: false,
      createdAt: new Date().toISOString(),
    };
    setCustomNotifications((prev) => [newNotif, ...prev]);
  }, []);

  const updatePreference = useCallback((type: NotificationType, channel: NotificationChannel, enabled: boolean) => {
    setPreferences((prev) => prev.map((p) => (p.type === type ? { ...p, [channel]: enabled } : p)));
  }, []);

  const getByType = useCallback((type: NotificationType) => notifications.filter((n) => n.type === type && !n.isDismissed), [notifications]);
  const getUnread = useCallback(() => notifications.filter((n) => !n.isRead && !n.isDismissed), [notifications]);

  return (
    <NotificationContext.Provider value={{
      notifications, preferences, unreadCount, criticalCount,
      markAsRead, markAllAsRead, dismiss, dismissAll,
      addNotification, updatePreference, getByType, getUnread,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
